import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/faq')({
  component: FAQPage,
})

function FAQPage() {
  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="terminal-border p-8 text-center">
          <h1 className="text-3xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
            [FREQUENTLY ASKED QUESTIONS]
          </h1>
          <p className="text-trail-tan font-mono">
            Learn how JingLearn's spaced repetition system works
          </p>
        </div>

        {/* How SuperMemo Works with JingLearn */}
        <div className="terminal-border border-terminal-green p-6">
          <h2 className="text-2xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
            🧠 How does the spaced repetition algorithm work?
          </h2>
          <div className="space-y-4 text-trail-tan font-mono text-sm leading-relaxed">
            <p>
              JingLearn uses a modified version of the <strong className="text-terminal-green">SuperMemo SM-2 algorithm</strong>,
              the same system used by popular apps like Anki. This algorithm schedules your flashcard reviews based on how well
              you know each word.
            </p>

            <div className="bg-black border-2 border-sky-blue p-4 mt-4">
              <h3 className="text-lg font-bold text-sky-blue mb-3">How it schedules your reviews:</h3>
              <ul className="space-y-2 list-none">
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">▸</span>
                  <div>
                    <strong className="text-sky-blue">New words:</strong> Start with a 1-day interval
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">▸</span>
                  <div>
                    <strong className="text-sky-blue">After first review:</strong> Interval increases to 6 days
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">▸</span>
                  <div>
                    <strong className="text-sky-blue">Subsequent reviews:</strong> Interval multiplies based on your "ease factor"
                    (how easy you find the word)
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">▸</span>
                  <div>
                    <strong className="text-sky-blue">Difficult words:</strong> Get reviewed more frequently (interval resets to 1 day)
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">▸</span>
                  <div>
                    <strong className="text-sky-blue">Easy words:</strong> Eventually reviewed months apart
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Grading System */}
        <div className="terminal-border border-sky-blue p-6">
          <h2 className="text-2xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
            📊 What do the grades mean?
          </h2>
          <div className="space-y-3">
            <div className="bg-black border-2 border-red-600 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-red-600 font-mono font-bold">Grade 0-1: Forgot / Hard</span>
                <span className="text-trail-tan text-xs font-mono">→ Reset to 1 day</span>
              </div>
              <p className="text-trail-tan text-xs font-mono">
                You didn't remember the word. It will be reviewed again tomorrow.
              </p>
            </div>

            <div className="bg-black border-2 border-orange-500 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-orange-500 font-mono font-bold">Grade 2: Again</span>
                <span className="text-trail-tan text-xs font-mono">→ Reset to 1 day</span>
              </div>
              <p className="text-trail-tan text-xs font-mono">
                You remembered with difficulty. Review again soon.
              </p>
            </div>

            <div className="bg-black border-2 border-blue-500 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-500 font-mono font-bold">Grade 3: Good</span>
                <span className="text-trail-tan text-xs font-mono">→ Standard interval increase</span>
              </div>
              <p className="text-trail-tan text-xs font-mono">
                You remembered correctly with some effort. Normal spacing progression.
              </p>
            </div>

            <div className="bg-black border-2 border-terminal-green p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-terminal-green font-mono font-bold">Grade 4-5: Easy / Perfect</span>
                <span className="text-trail-tan text-xs font-mono">→ Larger interval increase</span>
              </div>
              <p className="text-trail-tan text-xs font-mono">
                You remembered easily. The word will be reviewed less frequently.
              </p>
            </div>
          </div>
        </div>

        {/* Ease Factor Explanation */}
        <div className="terminal-border border-trail-tan p-6">
          <h2 className="text-2xl font-bold text-trail-tan mb-4 font-mono tracking-wider">
            ⚙️ What is the "Ease Factor"?
          </h2>
          <div className="space-y-4 text-trail-tan font-mono text-sm leading-relaxed">
            <p>
              The <strong className="text-terminal-green">ease factor</strong> is a multiplier that determines how quickly
              the interval grows between reviews. It starts at <strong>2.5</strong> for new cards.
            </p>
            <ul className="space-y-2 list-none pl-4">
              <li className="flex items-start gap-2">
                <span className="text-terminal-green">▸</span>
                <div>Easy reviews (grades 4-5) <strong>increase</strong> the ease factor → longer intervals</div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600">▸</span>
                <div>Difficult reviews (grades 0-2) <strong>decrease</strong> the ease factor → shorter intervals</div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sky-blue">▸</span>
                <div>Minimum ease factor is <strong>1.3</strong> (prevents cards from becoming impossibly difficult)</div>
              </li>
            </ul>
          </div>
        </div>

        {/* Projected Schedule */}
        <div className="terminal-border border-terminal-green p-6">
          <h2 className="text-2xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
            📅 How are projected review dates calculated?
          </h2>
          <div className="space-y-4 text-trail-tan font-mono text-sm leading-relaxed">
            <p>
              JingLearn shows you a <strong className="text-terminal-green">projected schedule</strong> of when your reviews
              will occur in the future. This projection assumes you'll continue to answer with <strong>Grade 4 (Easy)</strong> performance.
            </p>
            <p>
              <strong className="text-sky-blue">Why Grade 4?</strong> It represents consistent, successful learning.
              Your actual review schedule will adapt dynamically based on your real performance during reviews.
            </p>
            <p>
              The projection shows up to <strong>1 year ahead</strong> or <strong>50 reviews</strong>, whichever comes first.
            </p>
          </div>
        </div>

        {/* Why This Works */}
        <div className="terminal-border border-sky-blue p-6">
          <h2 className="text-2xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
            🔬 Why does spaced repetition work?
          </h2>
          <div className="space-y-4 text-trail-tan font-mono text-sm leading-relaxed">
            <p>
              Spaced repetition is based on decades of memory research. The key principles:
            </p>
            <ul className="space-y-3 list-none pl-4">
              <li className="flex items-start gap-2">
                <span className="text-terminal-green font-bold">1.</span>
                <div>
                  <strong className="text-terminal-green">The Spacing Effect:</strong> Reviewing information at increasing
                  intervals leads to better long-term retention than cramming.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terminal-green font-bold">2.</span>
                <div>
                  <strong className="text-terminal-green">Retrieval Practice:</strong> The act of recalling information
                  strengthens memory more than passive review.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-terminal-green font-bold">3.</span>
                <div>
                  <strong className="text-terminal-green">Desirable Difficulty:</strong> Reviewing just as you're about
                  to forget creates the strongest memory traces.
                </div>
              </li>
            </ul>
            <p className="text-xs text-gray-400 mt-4">
              Research by Cepeda et al. (2008), Karpicke & Roediger (2006), and Bjork & Bjork (2011)
            </p>
          </div>
        </div>

        {/* Back to App */}
        <div className="text-center">
          <a
            href="/app/flashcards"
            className="inline-flex items-center gap-2 px-6 py-3 bg-terminal-green/10 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-black transition-colors font-mono font-bold"
          >
            ← Back to Flashcards
          </a>
        </div>
      </div>
    </div>
  )
}
